import React from 'react';
import { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import { saveAs } from 'file-saver';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function RegistrationReport() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedOption, setSelectedOption] = React.useState('all');
  const [selectedClass, setSelectedClass] = React.useState('');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [ddlClass, setDdlClass] = useState([]);
  const [schoolData, setSchoolData] = useState([]);
  const [reportData, setReportData] = useState(null);

  const fetchSchoolDetails = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching financial years: ${response.status}`);
      }
      const data = await response.json();
      setSchoolData(data);
    } catch (error) {
      console.error(error);
    }
  };


  const fetchDdlClass = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      setDdlClass(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleClassChange = (event) => {
    setSelectedClass(event.target.value);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  const handleCancel = () => {
    // Handle cancel logic here
  };

  const formatDate = (dateString) => {
    const parts = dateString.split('-');
    const formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
    return formattedDate;
  };

  // ---------------------------------------------------------------------
  // Logo handling: the previous implementation tried to hot-link an image
  // using `${item.headerLogoImg}` appended to a hardcoded domain, which is
  // why the logo never rendered (wrong field / broken path / possible
  // CORS-blocked hotlinking). Just like AdmReport, we now fetch the school
  // record, pull the base64 logo out of it (`logoImg`), clean/pad it, and
  // embed it directly as a data URL. This guarantees the logo renders
  // in the print window without relying on an external image request.
  // ---------------------------------------------------------------------
  const getLogoDataUrl = async (schoolRecord) => {
    // 1x1 transparent PNG used as a safe fallback so the layout never breaks
    const fallbackLogo =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';

    try {
      const rawLogo =
        schoolRecord?.logoImg ||
        schoolRecord?.headerLogoImg ||
        schoolRecord?.logo ||
        '';

      if (!rawLogo || typeof rawLogo !== 'string') {
        return fallbackLogo;
      }

      // Already a usable URL or data URL - use as-is
      if (rawLogo.startsWith('http') || rawLogo.startsWith('data:image')) {
        return rawLogo;
      }

      // Otherwise treat it as a raw base64 string and clean/pad it
      let cleanedBase64 = rawLogo
        .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
        .replace(/\s/g, '')
        .replace(/\n/g, '');
      const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
      cleanedBase64 += '='.repeat(paddingLength);

      const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
      if (!isValidBase64 || cleanedBase64.length === 0) {
        return fallbackLogo;
      }

      const dataUrl = `data:image/png;base64,${cleanedBase64}`;

      // Validate that the image actually loads before using it
      const isValidImage = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = dataUrl;
      });

      return isValidImage ? dataUrl : fallbackLogo;
    } catch (error) {
      console.error('Error preparing logo image:', error);
      return fallbackLogo;
    }
  };

  const handleSave = async () => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/FeeReport/ReportRegistration`;
      const token = sessionStorage.getItem('token');
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      const requestBody = {
        classId: selectedClass || 0,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        isAll: selectedOption === 'all' ? true : false
      };
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        // Show an alert if the response status is not okay
        // alert(`Error: Please provide proper input`);
        setSnackbarMessage('Error: Please provide proper input!');
        setOpenSnackbar(true);

        return;
      }

      const data = await response.json();

      if (data.msg === "Record Not Found") {
        // Show an alert or handle the case where no records are found
        // alert('No records found');
        setSnackbarMessage('No records found!');
        setOpenSnackbar(true);
        return;
      }

      setReportData(data);

      // Resolve the school name/logo and the selected class label used
      // in the report header before we write anything to the new window.
      const schoolRecord = schoolData && schoolData[0] ? schoolData[0] : {};
      const schoolName = schoolRecord.schoolName || 'School Name';
      const logoDataUrl = await getLogoDataUrl(schoolRecord);
      const className =
        selectedOption === 'class' && selectedClass
          ? (ddlClass.find((c) => c.classId === selectedClass)?.className || 'All')
          : 'All Students';
      const reportTypeLabel = selectedOption === 'all' ? 'All Students' : `Class-wise (${className})`;
      const generatedOn = new Date().toLocaleString();

      const reportWindow = window.open('', '_blank');

      if (!reportWindow) {
        setSnackbarMessage('Please allow pop-ups to view the report!');
        setOpenSnackbar(true);
        return;
      }

      reportWindow.document.write(`
        <html>
          <head>
            <title>Registration Report</title>
            <style>

              * {
                box-sizing: border-box;
              }

              body {
                font-family: 'Segoe UI', Arial, sans-serif;
                background-color: #eef1f7;
                margin: 0;
                padding: 24px;
                color: #212121;
              }

              .report-container {
                background-color: #ffffff;
                max-width: 1100px;
                margin: 0 auto;
                border-radius: 10px;
                box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
                overflow: hidden;
              }

              .navbar {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 18px 24px;
                background: linear-gradient(90deg, #3f51b5 0%, #5c6bc0 100%);
                border-bottom: 4px solid #303f9f;
              }

              .logo-wrapper {
                width: 56px;
                height: 56px;
                background-color: #ffffff;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
              }

              .logo {
                max-width: 46px;
                max-height: 46px;
                object-fit: contain;
              }

              .header-text {
                flex-grow: 1;
                text-align: center;
              }

              .school-name {
                font-size: 22px;
                font-weight: 700;
                color: #ffffff;
                margin: 0;
                letter-spacing: 0.3px;
              }

              .report-title {
                font-size: 14px;
                font-weight: 500;
                color: #e8eaf6;
                margin: 2px 0 0 0;
                text-transform: uppercase;
                letter-spacing: 1.5px;
              }

              .meta-bar {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                justify-content: space-between;
                align-items: center;
                padding: 14px 24px;
                background-color: #f5f6fb;
                border-bottom: 1px solid #e0e0e0;
                font-size: 13px;
              }

              .meta-item {
                display: flex;
                flex-direction: column;
              }

              .meta-label {
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #757575;
                margin-bottom: 2px;
              }

              .meta-value {
                font-size: 13px;
                font-weight: 600;
                color: #303f9f;
              }

              .table-wrapper {
                padding: 20px 24px 8px 24px;
                overflow-x: auto;
              }

              table {
                border-collapse: collapse;
                width: 100%;
              }

              th, td {
                border: 1px solid #e0e0e0;
                text-align: left;
                padding: 10px 12px;
                font-size: 12.5px;
              }

              th {
                background-color: #673ab7;
                color: #ffffff;
                font-size: 13px;
                font-weight: 600;
                position: sticky;
                top: 0;
              }

              tbody tr:nth-child(even) {
                background-color: #f3f1fb;
              }

              tbody tr:hover {
                background-color: #ede7f6;
              }

              .footer-bar {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 24px 24px 24px;
                font-size: 12px;
                color: #757575;
              }

              .print-icon {
                margin-right: 6px;
              }

              .print-button {
                background-color: #4CAF50;
                border: none;
                color: white;
                padding: 10px 22px;
                text-align: center;
                text-decoration: none;
                font-size: 14px;
                font-weight: 600;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                align-items: center;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                transition: background-color 0.2s ease;
              }

              .print-button:hover {
                background-color: #43a047;
              }

              .print-button-container {
                display: flex;
                justify-content: flex-end;
                padding: 0 24px 24px 24px;
              }

              .no-data {
                padding: 24px;
                text-align: center;
                color: #757575;
                font-size: 14px;
              }

              @media print {
                body {
                  background-color: #ffffff;
                  padding: 0;
                }
                .report-container {
                  box-shadow: none;
                  border-radius: 0;
                  max-width: 100%;
                }
                .print-button-container {
                  display: none;
                }
                th {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                tbody tr:nth-child(even) {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .navbar {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }

            </style>
          </head>
          <body>
            <div class="report-container">
              <div class="navbar">
                <div class="logo-wrapper">
                  <img class="logo" src="${logoDataUrl}" alt="Logo" />
                </div>
                <div class="header-text">
                  <p class="school-name">${schoolName}</p>
                  <p class="report-title">Registration Report</p>
                </div>
              </div>

              <div class="meta-bar">
                <div class="meta-item">
                  <span class="meta-label">Report Type</span>
                  <span class="meta-value">${reportTypeLabel}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Date Range</span>
                  <span class="meta-value">${fromDate ? formatDate(fromDate) : ''} to ${toDate ? formatDate(toDate) : ''}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Total Records</span>
                  <span class="meta-value">${Array.isArray(data) ? data.length : 0}</span>
                </div>
              </div>

              <div class="table-wrapper">
                ${
                  Array.isArray(data) && data.length > 0
                    ? `
              <table>
                  <thead>
                    <tr>
                      <th>Registration Fee</th>
                      <th>Registration No</th>
                      <th>Name</th>
                      <th>Father Name</th>
                      <th>Mobile No</th>
                      <th>Email</th>
                      <th>Registration Date</th>
                      <th>Converted to Admission</th>
                      <th>Admission No</th>
                      <th>Admission Date</th>
                      <th>Registration Id</th>
                    </tr>
                  </thead>
                 <tbody>
                    ${data.map(item => `
                      <tr>
                        <td>${item.registrationFee ?? '-'}</td>
                        <td>${item.registrationNo ?? '-'}</td>
                        <td>${item.name ?? '-'}</td>
                        <td>${item.fatherName ?? '-'}</td>
                        <td>${item.mobileNo ?? '-'}</td>
                        <td>${item.email ?? '-'}</td>
                        <td>${item.registrationDate ?? '-'}</td>
                        <td>${item.convertedToAdmission === true ? 'Yes' : item.convertedToAdmission === false ? 'No' : '-'}</td>
                        <td>${item.admissionNo ?? '-'}</td>
                        <td>${item.admissionDate ?? '-'}</td>
                        <td>${item.regId ?? '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
                `
                    : `<div class="no-data">No records to display</div>`
                }
              </div>

              <div class="footer-bar">
                <span>Generated on: ${generatedOn}</span>
                <span>&copy; ${new Date().getFullYear()} ${schoolName}</span>
              </div>
            </div>

            <div class="print-button-container">
              <button class="print-button" onclick="window.print()">
                <span class="print-icon">&#128438;</span> Print
              </button>
            </div>
          </body>
        </html>
      `);

      // Without closing the document stream, some browsers keep the
      // window in a "loading" state which can make window.print() fire
      // before layout has fully settled (or silently no-op). Explicitly
      // closing it here is what makes the Print button work reliably.
      reportWindow.document.close();
      reportWindow.focus();
    } catch (error) {
      console.error('Error fetching report data:', error);
      setSnackbarMessage('Something went wrong while generating the report!');
      setOpenSnackbar(true);
    }
  };

  const generateCSV = () => {
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      console.error('No report data available');
      return;
    }

    let csvContent = '';
    const keys = Object.keys(reportData[0]);
    csvContent += keys.map(key => key.toUpperCase()).join(',') + '\n';
    reportData.forEach(item => {
      const values = keys.map(key => item[key]);
      csvContent += values.join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'report.csv');
  };

  const buttonStyles = {
    generate: {
      background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(33, 203, 243, .4)",
      },
    },
    csv: {
      background: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(244, 67, 54, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #d32f2f 30%, #e53935 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(244, 67, 54, .4)",
      },
    },
  };

  const selectStyles = {
    width: "250px",
    background: "linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)",
    borderRadius: "15px",
    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2196F3",
      borderWidth: "2px",
    },
    "&:hover": {
      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
      transform: "translateY(-2px)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1976D2",
      },
    },
    "&.Mui-focused": {
      boxShadow: "0 6px 12px rgba(33, 150, 243, 0.3)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#21CBF3",
      },
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontWeight: 500,
      color: "#333",
    },
  };

  // Function to format date to YYYY-MM-DD
  const getFormattedDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set default dates
  useEffect(() => {
    const today = new Date(); // Current date: March 29, 2025
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1); // Subtract 1 month

    setToDate(getFormattedDate(today)); // Default "To Date" to today
    setFromDate(getFormattedDate(oneMonthAgo)); // Default "From Date" to one month ago
    // Fetch data
    fetchDdlClass();
    fetchSchoolDetails();
  }, []);

  return (
    <Grid container justifyContent="center" style={{ margin: '0 auto', maxWidth: '700px' }}>
      <Grid item xs={12}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: '14px',
            padding: { xs: '20px', sm: '28px' },
            background: 'linear-gradient(145deg, #ffffff 0%, #f7f9fc 100%)',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: '#303f9f', textAlign: 'center', mb: 0.5 }}
          >
            Registration Report
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#757575', textAlign: 'center', mb: 2 }}
          >
            Generate and export student registration records
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <RadioGroup row aria-label="student" name="student" value={selectedOption} onChange={handleOptionChange}>
                  <FormControlLabel value="all" control={<Radio />} label="All Students" />
                  <FormControlLabel value="class" control={<Radio />} label="Class-wise Students" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {selectedOption === 'class' && (
                <FormControl fullWidth>
                  <Select value={selectedClass} onChange={handleClassChange} sx={selectStyles} displayEmpty>
                    <MenuItem value="" disabled>Select Class</MenuItem>
                    {ddlClass.map((classItem) => (
                      <MenuItem key={classItem.classId} value={classItem.classId}>
                        {classItem.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                sx={{ ...selectStyles, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                sx={{ ...selectStyles, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} container spacing={1} justifyContent="center" sx={{ marginBottom: 1, marginTop: 1 }}>
              <Grid item xs={6} sm={4} >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  fullWidth
                  size="small"
                  sx={{ ...buttonStyles.generate, minWidth: '150px' }}
                >
                  Generate Report
                </Button>
              </Grid>
              <Grid item xs={6} sm={2} >
                <Button
                  variant="contained"
                  color="error"
                  onClick={generateCSV}
                  fullWidth
                  size="small"
                  sx={{ ...buttonStyles.csv, minWidth: '150px' }}
                >
                  Download CSV
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default RegistrationReport;